import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Vollebak quick add (see snippets/vb-quick-add.liquid).
 *
 * Event-delegated: no per-card instances, so it costs nothing on grids that
 * load more cards later. Adds through the same contract as product-form.js —
 * FormData POST to Theme.routes.cart_add_url with the cart sections, then a
 * CartAddEvent carrying response.sections — so the cart drawer opens, the
 * count updates and other listeners fire exactly as from the product page.
 */

const ADDED_FLASH_MS = 1200;
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {HTMLDialogElement | null} */
let sheet = null;
let closeTimer = 0;
let busy = false;

/**
 * @param {string} variantId
 * @param {string} [productId]
 */
async function addToCart(variantId, productId) {
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const config = fetchConfig('javascript', { body: formData });
  const response = await fetch(Theme.routes.cart_add_url, {
    ...config,
    headers: { ...config.headers, Accept: 'text/html' },
  }).then((r) => r.json());

  if (response.status) throw new Error(response.message || response.description || 'Could not add to cart');

  document.dispatchEvent(
    new CartAddEvent({}, String(variantId), {
      source: 'vb-quick-add',
      itemCount: 1,
      productId,
      sections: response.sections,
    })
  );
}

/**
 * Live check against the product page. Whether a product can be bought is set
 * by its page template (staff swap the buy button for a waiting-list sign-up
 * in the theme editor), which a product card cannot see. The snippet excludes
 * the templates known to have no buy button; this catches any change made
 * since, so quick add can never put a waiting-list product in the cart.
 * One request per product the customer actually engages with, cached.
 */
const VERIFY_TTL_MS = 10 * 60 * 1000;
/** @type {Map<string, Promise<boolean>>} */
const verifying = new Map();

/** @param {HTMLElement} panel */
function verifyBuyable(panel) {
  const url = panel.dataset.productUrl;
  if (!url) return Promise.resolve(true);
  const known = verifying.get(url);
  if (known) return known;

  const key = `vbqa:buyable:${url}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (cached && Date.now() - cached.at < VERIFY_TTL_MS) {
      const hit = Promise.resolve(Boolean(cached.ok));
      verifying.set(url, hit);
      return hit;
    }
  } catch {
    // Storage unavailable; check live.
  }

  const check = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
    .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
    .then((html) => {
      const page = new DOMParser().parseFromString(html, 'text/html');
      const button = page.querySelector('add-to-cart-component button[name="add"]');
      const ok = button instanceof HTMLButtonElement && !button.disabled;
      try {
        sessionStorage.setItem(key, JSON.stringify({ ok, at: Date.now() }));
      } catch {
        // Storage unavailable; the in-memory map still holds the answer.
      }
      return ok;
    })
    // Could not check (offline, timeout): do not block a genuine purchase.
    .catch(() => { verifying.delete(url); return true; });

  verifying.set(url, check);
  return check;
}

/** Take quick add off a card whose product page has no buy button. @param {HTMLElement} panel */
function retire(panel) {
  panel.hidden = true;
  panel.classList.remove('is-kbd', 'is-kbd-card');
  panel.closest('product-card')?.querySelector('.vbqa-plus')?.setAttribute('hidden', '');
}

/** @type {HTMLElement | null} - The card panel the open sheet was built from. */
let sheetPanel = null;

/**
 * Shopify's own messages (e.g. stock limits) are shown as they are; a failed
 * request (offline, flaky signal) gets plain wording instead of the browser's.
 * @param {unknown} error
 */
function friendlyError(error) {
  const message = error instanceof Error ? error.message : '';
  const network = !navigator.onLine || error instanceof TypeError || /failed to fetch|load failed|network/i.test(message);
  if (network) return 'No connection. Check your signal and try again.';
  return message || 'Could not add to cart';
}

/**
 * Runs one add for a pressed control, with adding / added / error feedback.
 * @param {HTMLElement} control - The size button, bar or sheet add button pressed.
 * @param {string} variantId
 * @param {HTMLElement} scope - The panel or sheet that owns the status line.
 * @param {string} [productId]
 * @param {HTMLElement | null} [panel] - The card's panel, for the live buy-button check.
 */
async function runAdd(control, variantId, scope, productId, panel) {
  if (busy) return;
  busy = true;

  const status = scope.querySelector('.vbqa__status');
  const labelNode = control.querySelector('.vbqa__bar-label, .vbqa-sheet__add-label') ?? control;
  const original = labelNode.textContent;
  if (status) status.textContent = '';

  control.dataset.state = 'adding';
  labelNode.textContent = 'Adding';

  try {
    if (panel && !(await verifyBuyable(panel))) {
      // The product page no longer offers a buy button: send them there instead.
      delete control.dataset.state;
      labelNode.textContent = original;
      retire(panel);
      if (sheet?.open) closeSheet();
      if (panel.dataset.productUrl) window.location.href = panel.dataset.productUrl;
      return;
    }
    await addToCart(variantId, productId);
    control.dataset.state = 'added';
    labelNode.textContent = 'Added';
    if (sheet?.open) closeSheet();
    // A pointer press leaves focus on the control; release it so the desktop
    // panel lowers when the pointer moves away.
    if (document.activeElement === control && !control.matches(':focus-visible')) control.blur();
    window.setTimeout(() => {
      delete control.dataset.state;
      labelNode.textContent = original;
    }, ADDED_FLASH_MS);
  } catch (error) {
    delete control.dataset.state;
    labelNode.textContent = original;
    if (status) status.textContent = friendlyError(error);
  } finally {
    busy = false;
  }
}

function ensureSheet() {
  if (sheet) return sheet;

  sheet = document.createElement('dialog');
  sheet.className = 'vbqa-sheet';
  sheet.setAttribute('aria-labelledby', 'vbqa-sheet-title');
  sheet.innerHTML = `
    <div class="vbqa-sheet__panel" tabindex="-1">
      <div class="vbqa-sheet__head">
        <span class="vbqa-sheet__eyebrow">Quick add</span>
        <button type="button" class="vbqa-sheet__close" data-vbqa-close aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.25" fill="none"/></svg>
        </button>
      </div>
      <p class="vbqa-sheet__title" id="vbqa-sheet-title"></p>
      <div class="vbqa-sheet__body"></div>
      <p class="vbqa__status" role="status" aria-live="polite"></p>
    </div>`;

  sheet.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeSheet();
  });
  sheet.addEventListener('click', (event) => {
    if (event.target === sheet) closeSheet();
  });

  document.body.append(sheet);
  return sheet;
}

/** @param {HTMLElement} panel - The card's .vbqa panel (source of truth for sizes). */
function openSheet(panel) {
  const dialog = ensureSheet();
  clearTimeout(closeTimer);

  const title = dialog.querySelector('.vbqa-sheet__title');
  const body = dialog.querySelector('.vbqa-sheet__body');
  const status = dialog.querySelector('.vbqa__status');
  if (!title || !body) return;

  title.textContent = panel.dataset.productTitle ?? '';
  if (status) status.textContent = '';
  dialog.dataset.productId = panel.dataset.productId ?? '';
  sheetPanel = panel;
  verifyBuyable(panel).then((ok) => {
    if (ok || sheetPanel !== panel) return;
    retire(panel);
    if (dialog.open) closeSheet();
    if (panel.dataset.productUrl) window.location.href = panel.dataset.productUrl;
  });
  // Mirror the product page's buy button wording when the product has a
  // custom label (custom.add_to_cart_label, e.g. "Pre-order").
  const ctaLabel = panel.dataset.ctaLabel || '';
  const eyebrow = dialog.querySelector('.vbqa-sheet__eyebrow');
  if (eyebrow) eyebrow.textContent = ctaLabel || 'Quick add';

  const sizes = panel.querySelector('.vbqa__sizes');
  if (sizes) {
    body.replaceChildren(sizes.cloneNode(true));
  } else {
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'vbqa-sheet__add';
    add.dataset.vbqaSingle = panel.dataset.singleVariant ?? '';
    const label = document.createElement('span');
    label.className = 'vbqa-sheet__add-label';
    label.textContent = ctaLabel || 'Add to cart';
    const price = document.createElement('span');
    price.className = 'vbqa-sheet__add-price';
    price.textContent = panel.dataset.productPrice ?? '';
    add.append(label, price);
    body.replaceChildren(add);
  }

  if (!dialog.open) dialog.showModal();
  // Flush styles so the slide-up runs from the closed state (rAF stalls in background tabs).
  void dialog.offsetHeight;
  dialog.classList.add('is-open');
}

function closeSheet() {
  if (!sheet?.open) return;
  const dialog = sheet;
  dialog.classList.remove('is-open');
  clearTimeout(closeTimer);
  const finish = () => {
    if (dialog.open) dialog.close();
    if (document.activeElement instanceof HTMLElement && dialog.contains(document.activeElement)) document.activeElement.blur();
  };
  if (reduceMotion()) finish();
  else closeTimer = window.setTimeout(finish, 300);
}

// Fallback for browsers without overflow:clip (Safari < 16): if focus ever
// scrolls a clipped gallery to reveal the parked panel, put it straight back.
document.addEventListener(
  'scroll',
  (event) => {
    const el = event.target;
    if (!(el instanceof HTMLElement) || !el.classList.contains('card-gallery') || !el.querySelector('.vbqa')) return;
    if (el.scrollTop !== 0) el.scrollTop = 0;
    if (el.scrollLeft !== 0) el.scrollLeft = 0;
  },
  { capture: true, passive: true }
);

// The whole product card navigates on click, ignoring only real buttons. Keep
// clicks on the panel's non-button areas (padding, status) from navigating.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.vbqa') && !event.target.closest('button')) {
      event.stopPropagation();
      event.preventDefault();
    }
  },
  { capture: true }
);

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const closeBtn = event.target.closest('[data-vbqa-close]');
  if (closeBtn) return closeSheet();

  const plus = event.target.closest('[data-vbqa-plus]');
  if (plus) {
    const panel = plus.closest('product-card')?.querySelector('.vbqa');
    if (panel instanceof HTMLElement) openSheet(panel);
    return;
  }

  const size = event.target.closest('[data-vbqa-size]');
  if (size instanceof HTMLButtonElement) {
    if (size.disabled || !size.dataset.variantId) return;
    const scope = size.closest('.vbqa-sheet__panel, .vbqa');
    const productId = size.closest('.vbqa')?.dataset.productId ?? sheet?.dataset.productId;
    const owner = size.closest('.vbqa') ?? sheetPanel;
    if (scope instanceof HTMLElement) runAdd(size, size.dataset.variantId, scope, productId, owner instanceof HTMLElement ? owner : null);
    return;
  }

  const single = event.target.closest('[data-vbqa-single]');
  if (single instanceof HTMLElement && single.dataset.vbqaSingle) {
    const scope = single.closest('.vbqa-sheet__panel');
    if (scope instanceof HTMLElement) runAdd(single, single.dataset.vbqaSingle, scope, sheet?.dataset.productId, sheetPanel);
    return;
  }

  const bar = event.target.closest('[data-vbqa-bar]');
  if (bar instanceof HTMLElement) {
    const panel = bar.closest('.vbqa');
    if (!(panel instanceof HTMLElement)) return;
    if (panel.dataset.singleVariant) {
      runAdd(bar, panel.dataset.singleVariant, panel, panel.dataset.productId, panel);
    } else if (event.detail > 0) {
      // Pointer click: hover already governs the panel. Drop the focus the click
      // gave the button so nothing keeps it raised once the pointer leaves.
      panel.classList.remove('is-kbd', 'is-kbd-card');
      bar.setAttribute('aria-expanded', 'false');
      bar.blur();
    } else {
      // Keyboard activation (Enter/Space): keyboard focus already raises the panel
      // (:focus-visible), so just move into the sizes. Nothing is pinned.
      bar.setAttribute('aria-expanded', 'true');
      const first = panel.querySelector('.vbqa__size:not([disabled])');
      if (first instanceof HTMLElement) first.focus();
    }
  }
});

// Desktop: check the product page the first time the pointer enters a card, so
// a card whose page has just been switched to waiting list drops its bar.
/** @type {WeakSet<Element>} */
const hoverChecked = new WeakSet();
document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
  const card = event.target.closest('product-card');
  if (!card || hoverChecked.has(card)) return;
  hoverChecked.add(card);
  const panel = card.querySelector('.vbqa');
  if (panel instanceof HTMLElement) verifyBuyable(panel).then((ok) => { if (!ok) retire(panel); });
}, { passive: true });

// Keyboard users: raise the bar while keyboard focus is anywhere in the card,
// and the full panel while it is inside the panel. Pointer focus is ignored.
document.addEventListener('focusin', (event) => {
  if (!(event.target instanceof Element)) return;
  const panel = event.target.closest('product-card')?.querySelector('.vbqa');
  if (!panel) return;
  const keyboard = event.target.matches(':focus-visible');
  panel.classList.toggle('is-kbd-card', keyboard);
  panel.classList.toggle('is-kbd', keyboard && panel.contains(event.target));
});

// Focus moving into an iframe (e.g. the chat widget) does not reliably fire
// focusout in the parent document in Safari; the window's blur does.
window.addEventListener('blur', () => {
  document.querySelectorAll('.vbqa.is-kbd, .vbqa.is-kbd-card').forEach((panel) => panel.classList.remove('is-kbd', 'is-kbd-card'));
});

// Belt and braces for the iframe case: after every Tab, lower any panel whose
// card no longer holds focus (document.activeElement becomes the iframe).
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab') return;
  window.setTimeout(() => {
    document.querySelectorAll('.vbqa.is-kbd, .vbqa.is-kbd-card').forEach((panel) => {
      if (!panel.closest('product-card')?.contains(document.activeElement)) panel.classList.remove('is-kbd', 'is-kbd-card');
    });
  }, 0);
});

// Coming back from a product, the page loads with #<card-link-id> in the URL
// (the theme sets it before leaving so the card is scrolled into view). Safari
// focuses that fragment target, and the theme's focus ring is then painted
// around the whole card. Keep focus on a card link only when the keyboard put
// it there (a keydown just before); release any other focus, including focus
// kept across a back/forward-cache restore.
let vbqaLastKeyAt = 0;
document.addEventListener('keydown', (event) => {
  vbqaLastKeyAt = performance.now();
  // Keyboard navigation in use: let card links show the theme's focus ring
  // (hidden otherwise, see the stylesheet). Typing in a field does not count.
  const typing = event.target instanceof HTMLElement && event.target.matches('input, textarea, select, [contenteditable]');
  if (!typing) document.documentElement.classList.add('vb-kbd');
}, true);
document.addEventListener('pointerdown', () => document.documentElement.classList.remove('vb-kbd'), true);
const vbqaReleaseCardFocus = () => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !active.closest('product-card')) return;
  if (performance.now() - vbqaLastKeyAt < 1000) return;
  active.blur();
};
document.addEventListener('focusin', (event) => {
  if (event.target instanceof HTMLElement && event.target.matches('product-card a')) vbqaReleaseCardFocus();
});
window.addEventListener('pageshow', vbqaReleaseCardFocus);

// Land where you were. The theme scrolls the reopened grid so the card you
// left from sits at the top of the viewport; we remember where on screen the
// card was when you tapped it and put it back there.
const VBQA_RETURN_KEY = 'vbqa:return';
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('product-card');
  if (!card || event.target.closest('button, input, label, select')) return;
  const link = card.querySelector('.product-card__link');
  if (!(link instanceof HTMLElement) || !link.id) return;
  try {
    sessionStorage.setItem(VBQA_RETURN_KEY, JSON.stringify({ id: link.id, top: Math.round(card.getBoundingClientRect().top), at: Date.now() }));
  } catch {
    // Storage unavailable; the theme's card-at-top behaviour still applies.
  }
}, true);
let vbqaUserScrolled = false;
['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, () => { vbqaUserScrolled = true; }, { passive: true, capture: true }));
const vbqaRestoreReturn = () => {
  if (vbqaUserScrolled) return;
  let saved = null;
  try { saved = JSON.parse(sessionStorage.getItem(VBQA_RETURN_KEY) || 'null'); } catch { return; }
  if (!saved || location.hash !== '#' + saved.id || Date.now() - saved.at > 30 * 60 * 1000) return;
  const card = document.getElementById(saved.id)?.closest('product-card');
  if (!card) return;
  const delta = card.getBoundingClientRect().top - saved.top;
  if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'instant' });
};
const vbqaClearReturn = () => { try { sessionStorage.removeItem(VBQA_RETURN_KEY); } catch { /* ignore */ } };
// After the theme's own DOMContentLoaded scrollIntoView, then again once
// everything has loaded (Safari re-scrolls to the hash during load).
const vbqaOnReady = () => window.setTimeout(vbqaRestoreReturn, 0);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', vbqaOnReady); else vbqaOnReady();
const vbqaOnLoad = () => requestAnimationFrame(() => { vbqaRestoreReturn(); vbqaClearReturn(); });
if (document.readyState === 'complete') vbqaOnLoad(); else window.addEventListener('load', vbqaOnLoad);

document.addEventListener('focusout', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('product-card');
  if (!card || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;
  card.querySelector('.vbqa')?.classList.remove('is-kbd', 'is-kbd-card');
  card.querySelector('[data-vbqa-bar][aria-expanded="true"]')?.setAttribute('aria-expanded', 'false');
});
